import type { DrawingEntity, FurnitureObject, Vec3 } from '../types';

export const lineEndpoints = (drawing: DrawingEntity): [Vec3, Vec3] => drawing.points3d ?? [
  [drawing.points[0][0], 0, drawing.points[0][1]],
  [drawing.points[1][0], 0, drawing.points[1][1]],
];

export const drawingVertices3D = (drawing: DrawingEntity): Vec3[] => (drawing.kind === 'face' || drawing.kind === 'polyline') && drawing.vertices3d
  ? drawing.vertices3d
  : drawing.kind === 'line' ? lineEndpoints(drawing) : [];

export function drawingSnapPoints3D(drawing: DrawingEntity): Vec3[] {
  if (drawing.kind === 'circle') {
    const [center, edge] = drawing.points;
    const radius = Math.hypot(edge[0] - center[0], edge[1] - center[1]);
    return [[center[0], 0, center[1]], ...Array.from({ length: 8 }, (_, index) => [center[0] + Math.cos(index * Math.PI / 4) * radius, 0, center[1] + Math.sin(index * Math.PI / 4) * radius] as Vec3)];
  }
  const points: Vec3[] = drawing.vertices3d ?? (drawing.kind === 'line' ? lineEndpoints(drawing) : drawing.kind === 'rectangle' ? (() => { const [a, b] = drawing.points; return [[a[0], 0, a[1]], [b[0], 0, a[1]], [b[0], 0, b[1]], [a[0], 0, b[1]]] as Vec3[]; })() : drawing.points.map(([x, z]) => [x, 0, z] as Vec3));
  if (points.length < 2) return points;
  const closed = drawing.kind === 'face' || drawing.kind === 'rectangle';
  const result = [...points];
  const addEdgePoints = (a: Vec3, b: Vec3) => {
    for (const fraction of [.25, .5, .75]) result.push([a[0] + (b[0] - a[0]) * fraction, a[1] + (b[1] - a[1]) * fraction, a[2] + (b[2] - a[2]) * fraction]);
  };
  for (let index = 0; index < points.length - (closed ? 0 : 1); index += 1) {
    const a = points[index], b = points[(index + 1) % points.length];
    addEdgePoints(a, b);
  }
  if (drawing.kind === 'face' && drawing.extrusionMm && points.length >= 3) {
    const normal: Vec3 = [0, 0, 0];
    for (let index = 0; index < points.length; index += 1) {
      const a = points[index], b = points[(index + 1) % points.length];
      normal[0] += (a[1] - b[1]) * (a[2] + b[2]);
      normal[1] += (a[2] - b[2]) * (a[0] + b[0]);
      normal[2] += (a[0] - b[0]) * (a[1] + b[1]);
    }
    const length = Math.hypot(...normal);
    if (length > .001) {
      const sign = normal[1] < -.5 ? -1 : 1;
      const top = points.map((point) => point.map((coordinate, axis) => coordinate + normal[axis] / length * drawing.extrusionMm! * sign) as Vec3);
      result.push(...top);
      for (let index = 0; index < points.length; index += 1) {
        addEdgePoints(points[index], top[index]);
        addEdgePoints(top[index], top[(index + 1) % points.length]);
      }
    }
  }
  return result;
}

export function furnitureSnapPoints3D(item: FurnitureObject): Vec3[] {
  const cos = Math.cos(item.rotation[1]), sin = Math.sin(item.rotation[1]);
  const corners: Vec3[] = ([-1, 1] as const).flatMap((x) => ([-1, 1] as const).flatMap((z) => ([0, 1] as const).map((y) => [
    item.position[0] + x * item.width / 2 * cos - z * item.depth / 2 * sin,
    item.position[1] + y * item.height,
    item.position[2] + x * item.width / 2 * sin + z * item.depth / 2 * cos,
  ] as Vec3)));
  const result = [...corners];
  for (let a = 0; a < corners.length; a += 1) for (let b = a + 1; b < corners.length; b += 1) {
    const differences = (a ^ b).toString(2).replace(/0/g, '').length;
    if (differences !== 1) continue;
    for (const fraction of [.25, .5, .75]) result.push(corners[a].map((coordinate, axis) => coordinate + (corners[b][axis] - coordinate) * fraction) as Vec3);
  }
  return result;
}

export function nearestDrawingSnapPoint(drawings: DrawingEntity[], point: [number, number], radiusMm: number, furniture: FurnitureObject[] = []): Vec3 | null {
  let closest: Vec3 | null = null, distance = radiusMm;
  for (const drawing of drawings) for (const candidate of drawingSnapPoints3D(drawing).filter((candidate) => drawing.kind !== 'face' || !drawing.extrusionMm || drawing.vertices3d?.some((base) => Math.abs(base[1] - candidate[1]) < .001))) {
    const next = Math.hypot(candidate[0] - point[0], candidate[2] - point[1]);
    if (next <= distance) { closest = candidate; distance = next; }
  }
  for (const item of furniture) if (item.visible) for (const candidate of furnitureSnapPoints3D(item).filter((candidate) => Math.abs(candidate[1] - item.position[1]) < .001)) {
    const next = Math.hypot(candidate[0] - point[0], candidate[2] - point[1]);
    if (next <= distance) { closest = candidate; distance = next; }
  }
  return closest;
}

export const linePlanPoints = ([start, end]: [Vec3, Vec3]): [[number, number], [number, number]] => [
  [start[0], start[2]], [end[0], end[2]],
];

export function lineMetrics([start, end]: [Vec3, Vec3]) {
  const dx = end[0] - start[0], dy = end[1] - start[1], dz = end[2] - start[2];
  return {
    length: Math.hypot(dx, dy, dz),
    azimuth: Math.atan2(dz, dx) * 180 / Math.PI,
    elevation: Math.atan2(dy, Math.hypot(dx, dz)) * 180 / Math.PI,
  };
}

export function endpointFromAngles(start: Vec3, length: number, azimuth: number, elevation: number): Vec3 {
  const horizontal = length * Math.cos(elevation * Math.PI / 180);
  return [
    start[0] + horizontal * Math.cos(azimuth * Math.PI / 180),
    start[1] + length * Math.sin(elevation * Math.PI / 180),
    start[2] + horizontal * Math.sin(azimuth * Math.PI / 180),
  ];
}

/** Preserve the pointer's direction while using the exact entered length in millimetres. */
export function planEndpointAtLength(start: [number, number], toward: [number, number], length: number): [number, number] {
  const dx = toward[0] - start[0], dz = toward[1] - start[1];
  const distance = Math.hypot(dx, dz);
  return distance > 0 ? [start[0] + dx / distance * length, start[1] + dz / distance * length] : [...start];
}

export function nearestLineEndpoint(drawings: DrawingEntity[], point: [number, number], radiusMm: number): Vec3 | null {
  let closest: Vec3 | null = null;
  let distance = radiusMm;
  for (const drawing of drawings) {
    for (const endpoint of drawingVertices3D(drawing)) {
      const next = Math.hypot(endpoint[0] - point[0], endpoint[2] - point[1]);
      if (next <= distance) { closest = endpoint; distance = next; }
    }
  }
  return closest;
}

export const validLine = ([start, end]: [Vec3, Vec3]) =>
  [...start, ...end].every(Number.isFinite) && lineMetrics([start, end]).length >= .001;
